import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Modal, message, Select } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import { RootState } from '../../store/index';
import ServiceIndex from '../service/serviceIndex'
import ServiceDetail from '../service/serviceDetail'
import ServiceHome from '../service/serviceHome'
import { useCustomData } from '../../pages/context/serviceCustomDataProvider';
import { apiGetPage, apiAddInfo } from '../../request/api';

const formatDate = (date = new Date()) => {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    const hh = `${date.getHours()}`.padStart(2, '0');
    const mm = `${date.getMinutes()}`.padStart(2, '0');
    const ss = `${date.getSeconds()}`.padStart(2, '0');
    return `${y}-${m}-${d} ${hh}:${mm}:${ss}`;
};

const getLocalUserInfo = () => {
    return {
        userId: localStorage.getItem('userId') || '',
        userName: localStorage.getItem('userName') || '',
        userEmail: localStorage.getItem('userEmail') || '',
        userPhone: localStorage.getItem('userPhone') || '',
    };
};

// 服务类型映射
const getServiceStatus = (serviceType: string, languageCode: string): string => {
    if (languageCode === 'zh') {
        return serviceType; // 中文直接返回原值
    } else {
        // 英文映射
        const mapping: Record<string, string> = {
            '新开': 'newly opened',
            '续费': 'renew'
        };
        return mapping[serviceType] || serviceType; // 如果没有映射，返回原值
    }
};

const ServiceModalPlan: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const themeColor = useSelector((state: RootState) => state.themeColor.value);
    const [messageApi, contextHolder] = message.useMessage();
    const [selectedCompanyId, setSelectedCompanyId] = useState(localStorage.getItem('selectedCompanyId') || '')
    const userIds = localStorage.getItem('userId') || '';
    const [currentView, setCurrentView] = useState('workorder');
    const [viewData, setViewData] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isNoteOpen, setIsNoteOpen] = useState(false);
    const [noteType, setNoteType] = useState<'basic' | 'premium'>('basic');
    const [modalContent, setModalContent] = useState<any>(null);
    const languageCode = localStorage.getItem('siteLanguage') || 'zh';
    const [noteData, setNoteData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [companyList, setCompanyList] = useState<any[]>([]);

    const { getValue } = useCustomData();
    const serviceBusinessTap = getValue('serviceBusinessTap');
    const complateServiceInfo = getValue('complateServiceInfo');
    const serviceName = getValue('serviceName');
    const serviceVersion = getValue('serviceVersion');
    const serviceType = getValue('serviceType');
    const uploadInfo = getValue('uploadInfo');
    const notLogin = getValue('notLogin');
    const serviceCompany = getValue('serviceCompany');
    const plaseChosseCompany = getValue('plaseChosseCompany');
    const nothaveCompany = getValue('nothaveCompany');

    useEffect(() => {
        if (location.state?.resetView) {
            // 当收到重置标识时强制重置为service-home
            setCurrentView('service-home');
            // 清除state防止重复触发
            navigate(location.pathname, { replace: true, state: {} });
        } else if (location.state?.initialView) {
            setCurrentView(location.state.initialView);
        }
    }, [location.state]);

    useEffect(() => {
        if (!userIds) return; // 未登录就不请求
        apiGetPage({
            tid: 'E85B2C2CBF4C45FC89D7D540D35F780F',
            page: 1,
            size: 10,
            search: "belongToUser:" + userIds
        }).then(res => {
            const rows = res.data?.data?.rows || [];
            setCompanyList(rows);
        });
    }, [userIds]);

    const handleOpenModal = async (data: any, companyId?: string) => {
        setLoading(true);
        try {
            let finalContent = { ...data, serviceType: "新开", _forceServiceStatus: "新开" }; // 初始未知状态

            // 没有公司则不查
            const company = companyId || selectedCompanyId;
            if (!company) {
                finalContent = {
                    ...finalContent,
                    serviceType: languageCode === 'zh' ? '新开' : 'newly opened',
                    _forceServiceStatus: '新开'
                };
                setModalContent(finalContent);
                setIsModalOpen(true);
                setLoading(false);
                return;
            }

            setModalContent(finalContent);

            // 根据套餐 id 判断
            if (data.id === "D7074AA0788643E68CA1B6F03F996EB3") {
                // 特殊套餐逻辑
                const res = await apiGetPage({
                    tid: 'F32F1F7909694585A88B730331221DB3',
                    page: 1,
                    size: 10,
                    search:
                        "chooseCommodity=89762F6A770A46FE83B7A39C5230E371/chooseCommodity=D7074AA0788643E68CA1B6F03F996EB3," +
                        "orderStatus=已支付," +
                        "informeUser:" + userIds +
                        ",launchCompany:" + company
                });

                const rows = res.data?.data?.rows || [];
                const hasOld = rows.some((r: any) => r.chooseCommodity === "89762F6A770A46FE83B7A39C5230E371");
                const hasNew = rows.some((r: any) => r.chooseCommodity === "D7074AA0788643E68CA1B6F03F996EB3");

                if (hasOld && !hasNew) {
                    finalContent = { ...data, serviceType: languageCode === 'zh' ? '升级' : 'upgrade', _forceServiceStatus: '升级' };
                } else if (hasOld && hasNew) {
                    finalContent = { ...data, serviceType: languageCode === 'zh' ? '续费' : 'renew', _forceServiceStatus: '续费' };
                } else {
                    finalContent = { ...data, serviceType: languageCode === 'zh' ? '新开' : 'newly opened', _forceServiceStatus: '新开' };
                }
            } else {
                // 普通套餐逻辑
                const res = await apiGetPage({
                    tid: 'F32F1F7909694585A88B730331221DB3',
                    page: 1,
                    size: 10,
                    search: "chooseCommodity=" + data.id +
                        ",orderStatus=已支付," +
                        "informeUser:" + userIds +
                        ",launchCompany:" + company
                });

                const firstRow = res.data?.data?.rows?.[0];
                if (firstRow?.payAt) {
                    const payAt = new Date(firstRow.payAt.replace(/-/g, '/'));
                    const expireAt = new Date(payAt);
                    expireAt.setFullYear(expireAt.getFullYear() + 1);

                    if (new Date() < expireAt) {
                        finalContent = { ...data, serviceType: languageCode === 'zh' ? '续费' : 'renew', _forceServiceStatus: '续费' };
                    } else {
                        finalContent = { ...data, serviceType: languageCode === 'zh' ? '新开' : 'newly opened', _forceServiceStatus: '新开' };
                    }
                } else {
                    finalContent = { ...data, serviceType: languageCode === 'zh' ? '新开' : 'newly opened', _forceServiceStatus: '新开' };
                }
            }

            setModalContent(finalContent);
            setIsModalOpen(true);
        } catch (err) {
            message.error("加载失败，请稍后重试");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenNote = (data: any) => {
        setIsNoteOpen(true);
        setNoteData(data);
    };
    const normalizeServiceStatus = (serviceType: string, force?: string): string => {
        if (force) return force;
        const mapping: Record<string, string> = {
            '新开': '新开',
            '续费': '续费',
            'newly opened': '新开',
            'renew': '续费',
        };
        return mapping[serviceType] || serviceType;
    };

    const uploadInfoButton = () => {
        if (!userIds) {
            messageApi.error(notLogin);
            return;
        }

        const { userId, userName, userEmail, userPhone } = getLocalUserInfo();

        let serviceStatusValue = normalizeServiceStatus(
            modalContent.serviceType,
            modalContent._forceServiceStatus
        );

        let launchCompany = selectedCompanyId;
        if (!selectedCompanyId || selectedCompanyId === "") {
            launchCompany = "";
            serviceStatusValue = "新开";
        }

        const payload = {
            tid: 'DFB8D3686DDD4CE88ED9E7659257B8B4',
            launchCompany,
            serviceStatus: serviceStatusValue,
            createdTime: formatDate(new Date()),
            isIntention: '新开',
            chooseUser: `${userId}.${userName}`,
            contactEmail: userEmail,
            contactPhone: userPhone,
            workStatus: '待处理',
            languagecode: languageCode,
            chooseCommodity: modalContent.id,
        };

        apiAddInfo(payload)
            .then((res) => {
                const { ret, msg } = res.data || {};
                if (ret === '0') {
                    messageApi.success('提交成功');
                    setIsModalOpen(false);
                } else {
                    messageApi.error(msg || '提交失败');
                }
            })
            .catch(() => {
                messageApi.error('提交异常');
            });
    };

    const renderView = (openModal: (type: 'basic' | 'premium') => void) => {
        switch (currentView) {
            case 'service-home':
                return <ServiceHome
                    handleViewChange={handleViewChange}
                    data={viewData}
                    onOpenModal={handleOpenModal}
                    onOpenNote={handleOpenNote}
                />;
            case 'service-index':
                return <ServiceIndex
                    handleViewChange={handleViewChange}
                    data={viewData}
                    onOpenModal={handleOpenModal}
                    onOpenNote={handleOpenNote}
                />;
            case 'service-detail':
                return <ServiceDetail
                    handleViewChange={handleViewChange}
                    data={viewData}
                    onOpenModal={handleOpenModal}
                    onOpenNote={handleOpenNote}
                />;
            default:
                return <ServiceHome
                    handleViewChange={handleViewChange}
                    data={viewData}
                    onOpenModal={handleOpenModal}
                    onOpenNote={handleOpenNote}
                />;;
        }
    }

    const handleViewChange = (newView: string, data?: any) => {
        setCurrentView(newView);
        setViewData(data);
    };

    return (
        <div className="services-container">
            {contextHolder}
            <Modal
                open={isModalOpen}
                centered
                width={1200}
                closable={false}
                footer={null}
            >
                <div className="plan-content">
                    <div className='plan-content-header'>
                        <div>{complateServiceInfo}</div>
                        <CloseCircleOutlined onClick={() => {
                            setIsModalOpen(false)
                        }} style={{ fontSize: "35px", cursor: "pointer" }} />
                    </div>
                    <div className='plan-line'></div>
                    <div className='plan-info' style={{ alignItems: 'center' }}>
                        <span>{serviceCompany}</span>
                        <span style={{ color: themeColor }}>
                            <Select
                                value={selectedCompanyId || undefined}
                                onChange={(val) => {
                                    setSelectedCompanyId(val);
                                    localStorage.setItem('selectedCompanyId', val);
                                    if (modalContent) {
                                        handleOpenModal(modalContent, val);
                                    }
                                }}
                                disabled={!userIds || companyList.length === 0}
                                placeholder={
                                    !userIds
                                        ? notLogin
                                        : companyList.length === 0
                                            ? nothaveCompany
                                            : plaseChosseCompany
                                }
                                dropdownClassName="custom-select-dropdown"
                                style={{ minWidth: 240 }}
                                dropdownStyle={{ padding: 0 }}
                            >
                                {/* 新增暂无选项 */}
                                <Select.Option key="none" value="" style={{ fontSize: '14px' }}>
                                    暂无
                                </Select.Option>

                                {companyList.map((c) => (
                                    <Select.Option key={c.id} value={c.id} style={{ fontSize: '14px' }}>
                                        {c.componyName}
                                    </Select.Option>
                                ))}
                            </Select>
                        </span>
                    </div>

                    <div className='plan-info'>
                        <span>{serviceName}</span>
                        <span style={{ color: themeColor }}>{modalContent?.commodityType}</span>
                    </div>
                    <div className='plan-info'>
                        <span>{serviceVersion}</span>
                        <span style={{ color: themeColor }}>{modalContent?.commodityName}</span>
                    </div>
                    <div className='plan-info'>
                        <span>{serviceType}</span>
                        <span style={{ color: themeColor }}>{modalContent?.serviceType}</span>
                    </div>
                    <div className='plan-line'></div>
                    <Button onClick={() => uploadInfoButton()} style={{ borderRadius: '18px', marginTop: "0", backgroundColor: themeColor, boxShadow: `0 2px 4px 0 ` + themeColor, color: '#fff', border: 'none', width: "160px", height: "36px" }}>{uploadInfo}</Button>
                    <div style={{ marginTop: '16px', fontSize: "14px", color: "#33364b" }}>{serviceBusinessTap}</div>
                </div>
            </Modal>
            <Modal
                open={isNoteOpen}
                centered
                width={1200}
                closable={false}
                footer={null}
            >
                <div className="plan-content">
                    <div className='plan-content-header'>
                        <div>{noteData?.commodityType}</div>
                        <CloseCircleOutlined
                            onClick={() => setIsNoteOpen(false)}
                            style={{ fontSize: "35px", cursor: "pointer" }}
                        />
                    </div>
                    <div className='plan-line'></div>
                    <div
                        className="plan-note-body"
                        dangerouslySetInnerHTML={{ __html: noteData?.commodityRemark || '' }}
                    />
                </div>
            </Modal>

            {renderView(handleOpenModal)}
        </div>
    );
};

export default ServiceModalPlan;